class SihaBrandCarousel extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.scroller = this.querySelector('[data-siha-carousel-scroller]');
    this.previousButton = this.querySelector('[data-siha-carousel-previous]');
    this.nextButton = this.querySelector('[data-siha-carousel-next]');

    if (!this.scroller) return;

    this.initialized = true;
    this.autoplayEnabled = this.dataset.autoplay === 'true';
    this.autoplaySpeed = Math.max(Number(this.dataset.speed) || 4000, 3000);
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    this.handlePrevious = this.handlePrevious.bind(this);
    this.handleNext = this.handleNext.bind(this);
    this.pause = this.pause.bind(this);
    this.resume = this.resume.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.handleMotionChange = this.handleMotionChange.bind(this);
    this.update = this.update.bind(this);

    this.previousButton?.addEventListener('click', this.handlePrevious);
    this.nextButton?.addEventListener('click', this.handleNext);
    this.addEventListener('pointerenter', this.pause);
    this.addEventListener('pointerleave', this.resume);
    this.addEventListener('focusin', this.pause);
    this.addEventListener('focusout', this.handleFocusOut);
    this.scroller.addEventListener('touchstart', this.pause, { passive: true });
    this.scroller.addEventListener('touchend', this.resume, { passive: true });
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    if (this.reducedMotion.addEventListener) {
      this.reducedMotion.addEventListener('change', this.handleMotionChange);
    } else {
      this.reducedMotion.addListener(this.handleMotionChange);
    }

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.update);
      this.resizeObserver.observe(this.scroller);
    }

    requestAnimationFrame(() => {
      this.update();
      this.resume();
    });
  }

  disconnectedCallback() {
    this.pause();
    this.previousButton?.removeEventListener('click', this.handlePrevious);
    this.nextButton?.removeEventListener('click', this.handleNext);
    this.removeEventListener('pointerenter', this.pause);
    this.removeEventListener('pointerleave', this.resume);
    this.removeEventListener('focusin', this.pause);
    this.removeEventListener('focusout', this.handleFocusOut);
    this.scroller?.removeEventListener('touchstart', this.pause);
    this.scroller?.removeEventListener('touchend', this.resume);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.resizeObserver?.disconnect();

    if (this.reducedMotion?.removeEventListener) {
      this.reducedMotion.removeEventListener('change', this.handleMotionChange);
    } else {
      this.reducedMotion?.removeListener(this.handleMotionChange);
    }

    this.initialized = false;
  }

  get hasOverflow() {
    return this.scroller.scrollWidth - this.scroller.clientWidth > 4;
  }

  get scrollBehavior() {
    return this.reducedMotion.matches ? 'auto' : 'smooth';
  }

  get step() {
    const firstCard = this.scroller.querySelector('.siha-brand-showcase__card');
    if (!firstCard) return Math.max(this.scroller.clientWidth * 0.7, 1);

    return Math.max(firstCard.getBoundingClientRect().width * 2, 1);
  }

  update() {
    const showControls = this.hasOverflow;

    if (this.previousButton) this.previousButton.hidden = !showControls;
    if (this.nextButton) this.nextButton.hidden = !showControls;

    if (!showControls) this.pause();
  }

  move(direction) {
    if (!this.hasOverflow) return;

    const maximum = this.scroller.scrollWidth - this.scroller.clientWidth;
    const current = this.scroller.scrollLeft;
    let target = current + this.step * direction;

    if (direction > 0 && current >= maximum - 4) target = 0;
    if (direction < 0 && current <= 4) target = maximum;

    this.scroller.scrollTo({ left: target, behavior: this.scrollBehavior });
  }

  handlePrevious() {
    this.move(-1);
    this.restartAfterInteraction();
  }

  handleNext() {
    this.move(1);
    this.restartAfterInteraction();
  }

  pause() {
    window.clearInterval(this.autoplayTimer);
    window.clearTimeout(this.resumeTimer);
    this.autoplayTimer = undefined;
    this.resumeTimer = undefined;
  }

  resume() {
    this.pause();

    if (!this.autoplayEnabled || !this.hasOverflow || this.reducedMotion.matches || document.hidden) return;

    this.autoplayTimer = window.setInterval(() => this.move(1), this.autoplaySpeed);
  }

  restartAfterInteraction() {
    this.pause();
    this.resumeTimer = window.setTimeout(() => this.resume(), this.autoplaySpeed);
  }

  handleFocusOut(event) {
    if (!this.contains(event.relatedTarget)) this.resume();
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.pause();
    } else {
      this.resume();
    }
  }

  handleMotionChange() {
    if (this.reducedMotion.matches) {
      this.pause();
    } else {
      this.resume();
    }
  }
}

if (!customElements.get('siha-brand-carousel')) {
  customElements.define('siha-brand-carousel', SihaBrandCarousel);
}


